import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  keyframes,
} from '@mui/material';
import { CSSProperties, FC, ReactNode, useState } from 'react';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'src/hooks/useTranslationHook';

export type VerticalStepperCompleteResult = void | boolean | Promise<void | boolean>;

export type VerticalStepperStep = {
  label: string;
  component: ReactNode;
  description: string;
  completed: boolean;
  completeAction: () => VerticalStepperCompleteResult;
};

type VerticalStepperComponentProps = {
  steps: VerticalStepperStep[];
  finishButtonLabel: string;
  onCompleteMessage: ReactNode;
  /** Runs after the last step's `completeAction` succeeds (e.g. finalize PUT). May be async. */
  onCompleteHandler: () => void | Promise<void>;
  formId?: string;
  containerStyle?: CSSProperties;
  /**
   * Keep every step's content mounted while navigating (overrides MUI's default
   * `unmountOnExit`). Use when steps own local form state that must survive Back/Continue.
   */
  keepStepsMounted?: boolean;
};

const fadeInScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const VerticalStepperComponent: FC<VerticalStepperComponentProps> = ({
  steps,
  finishButtonLabel,
  onCompleteMessage,
  onCompleteHandler,
  formId = 'vertical-stepper-form',
  containerStyle,
  keepStepsMounted = false,
}) => {
  const { translateText } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [isCompletingStep, setIsCompletingStep] = useState(false);

  const handleNext = async () => {
    setIsCompletingStep(true);
    try {
      const result = await Promise.resolve(steps[activeStep].completeAction());
      if (result === false) {
        return;
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } finally {
      setIsCompletingStep(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    setIsCompletingStep(true);
    try {
      const result = await Promise.resolve(steps[activeStep].completeAction());
      if (result === false) {
        return;
      }
      await Promise.resolve(onCompleteHandler());
      setActiveStep(steps.length);
    } finally {
      setIsCompletingStep(false);
    }
  };

  return (
    <Box
      sx={{ ...containerStyle, position: 'relative', minHeight: '100%' }}
      id={formId}
      data-testid="vertical-stepper-container"
    >
      <Stepper 
        activeStep={activeStep} 
        orientation="vertical"
        sx={{
          '& .MuiStepLabel-root': {
            px: { xs: 1, sm: 2 },
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              optional={
                index === steps.length - 1 ? (
                  <Typography 
                    variant="caption"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {translateText('verticalStepper.lastStep')}
                  </Typography>
                ) : null
              }
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: { xs: 500, sm: 400 },
                },
              }}
            >
              {step.label}
            </StepLabel>
            <StepContent
              TransitionProps={
                keepStepsMounted ? { unmountOnExit: false } : undefined
              }
            >
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {step.description && (
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        mb: { xs: 1, sm: 2 },
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Grid>
                )}
                {step.component && (
                  <Grid item xs={12}>
                    {step.component}
                  </Grid>
                )}
                <Grid 
                  item 
                  xs={12}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    mt: { xs: 2, sm: 1 },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={
                      index === steps.length - 1 ? handleComplete : handleNext
                    }
                    sx={{ 
                      mt: { xs: 0, sm: 1 }, 
                      mr: { xs: 0, sm: 1 },
                      minHeight: { xs: '44px', sm: '36px' },
                      fontSize: { xs: '16px', sm: '14px' },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    disabled={!step.completed || isCompletingStep}
                    data-testid={`stepper-continue-button-${index}`}
                  >
                    {index === steps.length - 1
                      ? finishButtonLabel ||
                        translateText('verticalStepper.finish')
                      : translateText('verticalStepper.continue')}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ 
                      mt: { xs: 0, sm: 1 }, 
                      mr: { xs: 0, sm: 1 },
                      minHeight: { xs: '44px', sm: '36px' },
                      fontSize: { xs: '16px', sm: '14px' },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    data-testid={`stepper-back-button-${index}`}
                  >
                    {translateText('common.back')}
                  </Button>
                </Grid>
              </Grid>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length && (
        <Grid
          data-testid="complete-message"
          container
          sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            backgroundColor: 'rgba(255,255,255,0.8)',
            justifyContent: 'center',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <DoneAllIcon
            sx={{
              fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
              color: 'success.main',
              animation: `${fadeInScale} 0.6s ease-out`,
            }}
          />
          <Typography 
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textAlign: 'center',
              px: { xs: 2, sm: 0 },
            }}
          >
            {onCompleteMessage}
          </Typography>
        </Grid>
      )}
    </Box>
  );
};

export default VerticalStepperComponent;
